class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.activeTrigger = null;
    this.recommendationsDismissed = false;
    this.recommendationsRevealTimer = null;
    this.quantityTimers = new Map();

    this.discountStorageKey = 'amerce-cart-discount-code';
    this.appliedDiscountCode = this.getStoredDiscountCode();
    this.checkoutSubmitting = false;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onCartEvent = this.onCartEvent.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';

    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
    document.addEventListener('cart:refresh', this.onCartEvent);
    document.addEventListener('cart:updated', this.onCartEvent);
    document.addEventListener('product:added', this.onCartEvent);

    this.initializeShippingFields();
    this.syncAllQuantityButtons();
    this.syncRecommendationsPanel();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('submit', this.onSubmit);

    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('cart:refresh', this.onCartEvent);
    document.removeEventListener('cart:updated', this.onCartEvent);
    document.removeEventListener('product:added', this.onCartEvent);

    this.quantityTimers.forEach((timer) => window.clearTimeout(timer));
    this.quantityTimers.clear();

    window.clearTimeout(this.recommendationsRevealTimer);
  }

  get sectionId() {
    return this.dataset.sectionId || 'cart-drawer';
  }

  onDocumentClick(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-cart-drawer-open]');
    if (!trigger) return;

    event.preventDefault();
    this.open(trigger);
  }

  onDocumentKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      const openTool = this.querySelector('[data-cart-tool-panel]:not([hidden])');

      if (openTool) {
        this.closeTool(openTool);
      } else {
        this.close();
      }

      return;
    }

    if (event.key === 'Tab') this.trapFocus(event);
  }

  onCartEvent(event) {
    const detail = event.detail || {};
    const response = detail.response || detail.cart || detail;
    const openAfter = detail.open !== false;

    if (response?.sections?.[this.sectionId]) {
      this.renderContents(response, openAfter);
      return;
    }

    this.refresh(openAfter);
  }

  async onClick(event) {
    if (!(event.target instanceof Element)) return;

    const closeButton = event.target.closest('[data-cart-drawer-close]');
    if (closeButton) {
      event.preventDefault();
      this.close();
      return;
    }

    const recommendationsClose = event.target.closest('[data-cart-recommendations-close]');
    if (recommendationsClose) {
      event.preventDefault();
      this.dismissRecommendations();
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity-button]');
    if (quantityButton) {
      event.preventDefault();
      this.handleQuantityButton(quantityButton);
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      event.preventDefault();

      const item = removeButton.closest('[data-cart-item]');
      if (item) await this.changeLine(item.dataset.lineKey, 0, removeButton);
      return;
    }

    const toolOpenButton = event.target.closest('[data-cart-tool-open]');
    if (toolOpenButton) {
      event.preventDefault();
      this.openTool(toolOpenButton.dataset.cartToolOpen, toolOpenButton);
      return;
    }

    const toolCloseButton = event.target.closest('[data-cart-tool-close]');
    if (toolCloseButton) {
      event.preventDefault();
      this.closeTool(toolCloseButton.closest('[data-cart-tool-panel]'));
    }
  }

  onChange(event) {
    if (!(event.target instanceof Element)) return;

    const quantityInput = event.target.closest('[data-cart-quantity]');
    if (quantityInput) {
      this.handleQuantityInput(quantityInput);
      return;
    }

    const giftWrapCheckbox = event.target.closest('[data-cart-gift-wrap]');
    if (giftWrapCheckbox) {
      this.handleGiftWrapChange(giftWrapCheckbox);
      return;
    }

    if (event.target.matches('[data-shipping-country]')) {
      this.populateProvinces(event.target);
    }
  }

  async handleGiftWrapChange(checkbox) {
    const keepOpen = this.isOpen;

    this.setLoading(checkbox, true);

    try {
      if (checkbox.checked) {
        const variantId = checkbox.dataset.variantId;
        if (!variantId) throw new Error(this.dataset.genericError);

        const response = await this.postJSON(this.dataset.cartAddUrl, {
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: { _gift_wrap: 'true' },
            },
          ],
          sections: [this.sectionId],
          sections_url: window.location.pathname,
        });

        this.renderContents(response, keepOpen);
        this.dispatchCartUpdated(response);
      } else {
        const lineKey = this.findGiftWrapLineKey();
        if (lineKey) await this.changeLine(lineKey, 0);
      }
    } catch (error) {
      checkbox.checked = !checkbox.checked;
      this.handleError(error);
    } finally {
      if (checkbox.isConnected) this.setLoading(checkbox, false);
    }
  }

  findGiftWrapLineKey() {
    const item = this.querySelector('[data-cart-item][data-gift-wrap="true"]');
    return item?.dataset.lineKey || null;
  }

  async onSubmit(event) {
    if (!(event.target instanceof Element)) return;

    const noteForm = event.target.closest('[data-cart-note-form]');
    if (noteForm) {
      event.preventDefault();
      await this.saveNote(noteForm);
      return;
    }

    const shippingForm = event.target.closest('[data-cart-shipping-form]');
    if (shippingForm) {
      event.preventDefault();
      await this.calculateShipping(shippingForm);
      return;
    }

    const discountForm = event.target.closest('[data-cart-discount-form]');
    if (discountForm) {
      event.preventDefault();
      await this.applyDiscount(discountForm);
      return;
    }

    const checkoutForm = event.target.closest('[data-cart-checkout-form]');
    if (!checkoutForm) return;

    const terms = checkoutForm.querySelector('[data-cart-terms]');
    if (terms && !terms.checked) {
      event.preventDefault();
      terms.focus();
      this.setStatus(
        this.dataset.termsError || 'Please accept the terms and conditions.',
        true,
      );
      return;
    }

    if (this.checkoutSubmitting) {
      event.preventDefault();
      return;
    }

    const discount = this.getStoredDiscountCode();

    /*
     * Nếu không có mã giảm giá được nhập qua cart drawer,
     * để Shopify submit checkout theo hành vi mặc định.
     */
    if (!discount) return;

    event.preventDefault();

    await this.checkoutWithDiscount(
      checkoutForm,
      event.submitter,
      discount,
    );
  }

  open(trigger = null, options = {}) {
    if (this.dataset.cartType === 'page') return;

    const { focusClose = true } = options;
    const wasOpen = this.isOpen;

    this.activeTrigger = trigger || this.activeTrigger || document.activeElement;

    if (!wasOpen) {
      this.recommendationsDismissed = false;
    }

    this.hidden = false;
    this.setAttribute('aria-hidden', 'false');
    this.setTriggersExpanded(true);
    this.syncRecommendationsPanel();

    if (!wasOpen) {
      // Only once the drawer is no longer [hidden] (so it's actually
      // rendered/painted) does resetting the bar's width and animating it
      // back up have anything to visibly animate.
      this.revealProgressBar();
    }

    const recommendationsPanel = this.querySelector('[data-cart-recommendations-panel]');
    window.clearTimeout(this.recommendationsRevealTimer);

    /*
     * renderContents() gọi lại open() mỗi khi nội dung cart refresh (đổi số
     * lượng, xoá item...) trong lúc drawer đang mở, không chỉ lúc mở từ
     * đóng. Chỉ chạy hiệu ứng ẩn-rồi-hiện-sau cho lần mở thật sự (wasOpen
     * false) — nếu không, mỗi lần refresh nội dung sẽ ẩn panel rồi hẹn giờ
     * lại, và bấm +/- liên tục trong lúc timer chưa chạy xong sẽ làm panel
     * kẹt luôn ở trạng thái ẩn.
     */
    if (wasOpen) {
      recommendationsPanel?.classList.add('is-revealed');
    } else {
      recommendationsPanel?.classList.remove('is-revealed');
    }

    requestAnimationFrame(() => {
      this.classList.add('show');
      this.isOpen = true;

      document.documentElement.classList.add('overflow-hidden');
      document.body.classList.add('overflow-hidden');

      this.createBackdrop();
      this.syncAllQuantityButtons();

      if (focusClose) {
        this.querySelector('[data-cart-drawer-close]')?.focus({ preventScroll: true });
      }

      /*
       * Panel gợi ý trượt vào sau khi khối giỏ hàng chính đã ổn định
       * (0.35s là thời lượng transition của chính cart-drawer), thay vì
       * trượt cùng lúc như một khối cứng — nhẹ và ít giật hơn.
       */
      if (!wasOpen && recommendationsPanel && !recommendationsPanel.hidden) {
        const prefersReducedMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)',
        ).matches;

        this.recommendationsRevealTimer = window.setTimeout(
          () => recommendationsPanel.classList.add('is-revealed'),
          prefersReducedMotion ? 0 : 350,
        );
      }
    });
  }

  close() {
    this.classList.remove('show');
    this.setAttribute('aria-hidden', 'true');
    this.isOpen = false;

    window.clearTimeout(this.recommendationsRevealTimer);
    this.querySelector('[data-cart-recommendations-panel]')?.classList.remove('is-revealed');

    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');

    this.setTriggersExpanded(false);
    this.removeBackdrop();

    window.setTimeout(() => {
      if (!this.isOpen) this.hidden = true;
    }, 300);

    if (this.activeTrigger instanceof HTMLElement) {
      this.activeTrigger.focus({ preventScroll: true });
    }
  }

  /*
   * Opened because the merchant selected the section in the theme editor, not
   * because a visitor clicked the cart trigger: focus is left in the editor's own
   * panel instead of being pulled into the preview iframe, and no trigger is
   * recorded, so closing it again hands focus nowhere rather than jumping the
   * preview to the header.
   *
   * The cart-type gate in open() still applies — with the cart set to a page
   * there is no drawer to preview.
   */
  openFromEditor() {
    this.open(null, { focusClose: false });
    this.activeTrigger = null;
  }

  closeFromEditor() {
    if (!this.isOpen) return;
    this.close();
  }

  setTriggersExpanded(expanded) {
    document.querySelectorAll('[data-cart-drawer-open]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  createBackdrop() {
    if (document.querySelector('[data-cart-drawer-backdrop]')) return;

    const backdrop = document.createElement('div');
    backdrop.className = 'offcanvas-backdrop fade show';
    backdrop.dataset.cartDrawerBackdrop = '';
    backdrop.addEventListener('click', () => this.close(), { once: true });
    document.body.append(backdrop);
  }

  removeBackdrop() {
    document.querySelector('[data-cart-drawer-backdrop]')?.remove();
  }

  dismissRecommendations() {
    this.recommendationsDismissed = true;
    this.syncRecommendationsPanel();
  }

  syncRecommendationsPanel() {
    const panel = this.querySelector('[data-cart-recommendations-panel]');
    const hasProducts = Boolean(panel?.querySelector('.list-cart-item'));
    const shouldShow = Boolean(panel && hasProducts && !this.recommendationsDismissed);

    if (panel) {
      panel.hidden = !shouldShow;
      panel.setAttribute('aria-hidden', String(!shouldShow));
    }

    this.classList.toggle('recommendations-hidden', !shouldShow);
  }

  trapFocus(event) {
    const focusable = this.getFocusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  getFocusableElements() {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    return Array.from(this.querySelectorAll(selector)).filter(
      (element) => !element.hidden && element.offsetParent !== null,
    );
  }

  handleQuantityButton(button) {
    if (button.disabled) return;

    const item = button.closest('[data-cart-item]');
    const input = item?.querySelector('[data-cart-quantity]');
    if (!item || !input) return;

    const bounds = this.getQuantityBounds(input);
    const current = this.getInputQuantity(input, bounds.min);
    const direction = button.dataset.action === 'increase' ? 1 : -1;
    const next = this.normalizeQuantity(current + direction * bounds.step, bounds);

    if (next === current) {
      this.syncQuantityButtons(item, current);
      return;
    }

    input.value = String(next);
    this.syncQuantityButtons(item, next);
    this.debounceQuantityChange(item.dataset.lineKey, next, button);
  }

  handleQuantityInput(input) {
    const item = input.closest('[data-cart-item]');
    if (!item) return;

    const bounds = this.getQuantityBounds(input);
    const entered = this.getInputQuantity(input, bounds.min);
    const quantity = this.normalizeQuantity(entered, bounds);

    input.value = String(quantity);
    this.syncQuantityButtons(item, quantity);
    this.debounceQuantityChange(item.dataset.lineKey, quantity, input);
  }

  getQuantityBounds(input) {
    const min = Number.parseInt(input.min, 10);
    const max = Number.parseInt(input.max, 10);
    const step = Number.parseInt(input.step, 10);

    return {
      min: Number.isNaN(min) ? 1 : min,
      max: Number.isNaN(max) ? Number.POSITIVE_INFINITY : max,
      step: Number.isNaN(step) || step <= 0 ? 1 : step,
    };
  }

  getInputQuantity(input, fallback = 1) {
    const quantity = Number.parseInt(input.value, 10);
    return Number.isNaN(quantity) ? fallback : quantity;
  }

  normalizeQuantity(quantity, bounds) {
    let normalized = Number.isFinite(quantity) ? quantity : bounds.min;

    normalized = Math.max(bounds.min, normalized);
    if (Number.isFinite(bounds.max)) normalized = Math.min(bounds.max, normalized);

    const stepCount = Math.floor((normalized - bounds.min) / bounds.step);
    normalized = bounds.min + Math.max(0, stepCount) * bounds.step;

    if (Number.isFinite(bounds.max)) normalized = Math.min(bounds.max, normalized);

    return normalized;
  }

  syncQuantityButtons(item, quantity = null) {
    const input = item?.querySelector('[data-cart-quantity]');
    if (!input) return;

    const bounds = this.getQuantityBounds(input);
    const current = quantity ?? this.getInputQuantity(input, bounds.min);

    const decrease = item.querySelector(
      '[data-cart-quantity-button][data-action="decrease"]',
    );
    const increase = item.querySelector(
      '[data-cart-quantity-button][data-action="increase"]',
    );

    if (decrease) decrease.disabled = current <= bounds.min;
    if (increase) {
      increase.disabled = Number.isFinite(bounds.max) && current >= bounds.max;
    }
  }

  syncAllQuantityButtons() {
    this.querySelectorAll('[data-cart-item]').forEach((item) => {
      this.syncQuantityButtons(item);
    });
  }

  debounceQuantityChange(lineKey, quantity, control) {
    if (!lineKey) return;

    window.clearTimeout(this.quantityTimers.get(lineKey));

    const timer = window.setTimeout(() => {
      this.quantityTimers.delete(lineKey);
      this.changeLine(lineKey, quantity, control);
    }, 350);

    this.quantityTimers.set(lineKey, timer);
  }

  async changeLine(lineKey, quantity, control) {
    if (!lineKey) return;

    const keepOpen = this.isOpen;

    const item =
      control?.closest('[data-cart-item]') ||
      this.querySelector(
        `[data-cart-item][data-line-key="${this.escapeSelector(lineKey)}"]`,
      );

    window.clearTimeout(
      this.quantityTimers.get(lineKey),
    );

    this.quantityTimers.delete(lineKey);

    this.setLoading(control, true);
    this.setItemLoading(item, true);
    this.setDrawerBusy(true);

    try {
      const response = await this.postJSON(
        this.dataset.cartChangeUrl,
        {
          id: lineKey,
          quantity,
          sections: [this.sectionId],
          sections_url: window.location.pathname,
        },
      );

      this.renderContents(response, keepOpen);
      this.dispatchCartUpdated(response);
    } catch (error) {
      this.handleError(error);

      if (item?.isConnected) {
        this.setItemLoading(item, false);
      }

      await this.refresh(keepOpen);
    } finally {
      if (control?.isConnected) {
        this.setLoading(control, false);
      }

      if (item?.isConnected) {
        this.setItemLoading(item, false);
      }

      this.setDrawerBusy(false);
    }
  }

  async saveNote(form) {
    const submit = form.querySelector('[type="submit"]');
    const note = form.elements.note?.value || '';

    this.setLoading(submit, true);

    try {
      const response = await this.postJSON(this.dataset.cartUpdateUrl, {
        note,
        sections: [this.sectionId],
        sections_url: window.location.pathname,
      });

      this.renderContents(response, true);
      this.dispatchCartUpdated(response);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoading(submit, false);
    }
  }

  async applyDiscount(form) {
    const submit = form.querySelector('[type="submit"]');
    const discountInput = form.elements.discount;
    const discount =
      typeof discountInput?.value === 'string'
        ? discountInput.value.trim()
        : '';

    this.setLoading(submit, true);
    this.setStatus('');

    try {
      const response = await this.postJSON(this.dataset.cartUpdateUrl, {
        discount,
        sections: [this.sectionId],
        sections_url: window.location.pathname,
      });

      /*
       * Lưu mã sau khi Shopify cập nhật cart thành công.
       * Mã này sẽ được gửi lại ngay trước khi checkout.
       */
      this.saveDiscountCode(discount);

      this.renderContents(response, true);
      this.dispatchCartUpdated(response);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoading(submit, false);
    }
  }

  async calculateShipping(form) {
    const submit = form.querySelector('[type="submit"]');
    const errorBox = form.querySelector('[data-shipping-error]');
    const successBox = form.querySelector('[data-shipping-success]');

    if (!errorBox || !successBox) return;

    const params = new URLSearchParams();
    new FormData(form).forEach((value, name) => {
      if (value) params.append(name, String(value));
    });

    errorBox.hidden = true;
    successBox.hidden = false;
    successBox.textContent = this.dataset.shippingPending || '';
    this.setLoading(submit, true);

    try {
      const response = await fetch(`${this.dataset.shippingPrepareUrl}?${params}`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw await this.createRequestError(response);

      const rates = await this.pollShippingRates(params);
      this.renderShippingRates(successBox, rates);
    } catch (error) {
      successBox.hidden = true;
      errorBox.hidden = false;
      errorBox.textContent = error.message || this.dataset.genericError;
    } finally {
      this.setLoading(submit, false);
    }
  }

  async pollShippingRates(params) {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const response = await fetch(`${this.dataset.shippingRatesUrl}?${params}`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw await this.createRequestError(response);

      const result = await response.json();
      if (result?.shipping_rates) return result.shipping_rates;

      await new Promise((resolve) => window.setTimeout(resolve, 500));
    }

    return [];
  }

  renderShippingRates(container, rates) {
    container.replaceChildren();

    if (!rates.length) {
      container.textContent = this.dataset.shippingEmpty || '';
      return;
    }

    const list = document.createElement('ul');
    list.setAttribute('role', 'list');

    rates.forEach((rate) => {
      const item = document.createElement('li');
      const name = document.createElement('span');
      const price = document.createElement('span');

      name.textContent = rate.presentment_name || rate.name;
      price.textContent = this.formatMajorMoney(
        rate.price,
        rate.currency || this.dataset.cartCurrency,
      );

      item.append(name, document.createTextNode(' — '), price);
      list.append(item);
    });

    container.append(list);
  }

  formatMajorMoney(value, currency) {
    const amount = Number.parseFloat(value || '0');

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || this.dataset.cartCurrency || 'USD',
      }).format(amount);
    } catch (error) {
      return `${amount.toFixed(2)} ${currency || ''}`.trim();
    }
  }

  openTool(name, trigger) {
    if (!name) return;

    this.querySelectorAll('[data-cart-tool-panel]').forEach((panel) => {
      const active = panel.dataset.cartToolPanel === name;
      panel.hidden = !active;
      panel.classList.toggle('open', active);
    });

    const panel = this.querySelector(
      `[data-cart-tool-panel="${this.escapeSelector(name)}"]`,
    );

    if (!panel) return;

    panel.hidden = false;
    panel.classList.add('open');
    panel.querySelector('textarea, input, select, button')?.focus({ preventScroll: true });
  }

  closeTool(panel) {
    if (!panel) return;

    const toolName = panel.dataset.cartToolPanel;
    panel.classList.remove('open');

    window.setTimeout(() => {
      if (!panel.classList.contains('open')) panel.hidden = true;
    }, 300);

    if (!toolName) return;

    this.querySelector(
      `[data-cart-tool-open="${this.escapeSelector(toolName)}"]`,
    )?.focus({ preventScroll: true });
  }

  initializeShippingFields() {
    const country = this.querySelector('[data-shipping-country]');
    if (country) this.populateProvinces(country);
  }

  populateProvinces(countrySelect) {
    const provinceSelect = this.querySelector('[data-shipping-province]');
    const provinceWrap = this.querySelector('[data-shipping-province-wrap]');

    if (!provinceSelect || !provinceWrap) return;

    const selectedOption = countrySelect.options[countrySelect.selectedIndex];
    const provinces = this.readJSONAttribute(selectedOption?.dataset.provinces, []);

    provinceSelect.replaceChildren();

    provinces.forEach(([label, value]) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      provinceSelect.append(option);
    });

    const hasProvinces = provinces.length > 0;
    provinceWrap.hidden = !hasProvinces;
    provinceSelect.disabled = !hasProvinces;
  }

  async refresh(openAfter = false) {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', this.sectionId);

    try {
      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw await this.createRequestError(response);

      const sections = await response.json();
      this.renderContents({ sections }, openAfter);
    } catch (error) {
      this.handleError(error);
    }
  }

  renderContents(response, openAfter = true) {
    const sectionHTML = response?.sections?.[this.sectionId];
    if (!sectionHTML) return;

    const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const nextDrawer = parsed.querySelector('cart-drawer');
    if (!nextDrawer) return;

    const wasOpen = this.isOpen;

    const previousProgressValue = this.querySelector('.tf-progress-ship .value');
    const previousProgressWidth = previousProgressValue?.style.width || null;

    this.innerHTML = nextDrawer.innerHTML;
    this.replaceDataAttributes(nextDrawer);
    this.dataset.initialized = 'true';

    if (wasOpen) {
      // Drawer was already open and visible - animate the width CHANGE
      // between the old and new totals. If it wasn't open, open() below
      // handles revealing the bar with its own fill-in animation instead.
      this.animateProgressBar(previousProgressWidth);
    }

    this.initializeShippingFields();
    this.syncAllQuantityButtons();
    this.syncRecommendationsPanel();

    const itemCount = response.item_count ?? Number(this.dataset.cartItemCount || 0);
    this.updateCartCount(itemCount);

    if (openAfter || wasOpen) {
      this.open(this.activeTrigger, { focusClose: false });
    }
  }

  // renderContents() replaces the drawer's innerHTML wholesale on every cart
  // update, so the free-shipping bar's fill is a brand-new element already
  // at its final width - the CSS transition on .value never has a "from"
  // state to animate from and just snaps. Re-run the transition manually:
  // set it back to the previous width, force a reflow, then let it animate
  // forward to the new width.
  animateProgressBar(previousWidth) {
    if (!previousWidth) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const value = this.querySelector('.tf-progress-ship .value');
    if (!value) return;

    const targetWidth = value.style.width;
    if (!targetWidth || targetWidth === previousWidth) return;

    value.style.transition = 'none';
    value.style.width = previousWidth;

    // Force a reflow so the browser registers the starting width before the
    // transition is re-enabled and the target width is applied.
    void value.offsetWidth;

    value.style.transition = '';
    value.style.width = targetWidth;
  }

  // Runs whenever the drawer actually opens (not on a content refresh while
  // already open, see the wasOpen guard at the call site) so the
  // free-shipping bar fills in from empty each time - the same reflow-
  // restart trick as animateProgressBar, just always starting from 0%.
  revealProgressBar() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const value = this.querySelector('.tf-progress-ship .value');
    if (!value) return;

    const targetWidth = value.style.width;
    if (!targetWidth || targetWidth === '0%') return;

    value.style.transition = 'none';
    value.style.width = '0%';

    void value.offsetWidth;

    value.style.transition = '';
    value.style.width = targetWidth;
  }

  replaceDataAttributes(source) {
    Array.from(this.attributes)
      .filter((attribute) => attribute.name.startsWith('data-'))
      .forEach((attribute) => this.removeAttribute(attribute.name));

    Array.from(source.attributes)
      .filter((attribute) => attribute.name.startsWith('data-'))
      .forEach((attribute) => this.setAttribute(attribute.name, attribute.value));
  }

  updateCartCount(count) {
    const normalized = Number.parseInt(count, 10) || 0;

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(normalized);
      element.hidden = normalized === 0;
    });

    document.querySelectorAll('[data-cart-count-text]').forEach((element) => {
      const template = element.dataset.cartCountTextTemplate;
      element.textContent = template
        ? template.replace('[count]', String(normalized))
        : String(normalized);
    });
  }

  dispatchCartUpdated(response) {
    document.dispatchEvent(
      new CustomEvent('cart:rendered', {
        detail: { response, source: 'cart-drawer' },
      }),
    );
  }

  saveDiscountCode(code) {
    const normalizedCode = String(code || '').trim();

    this.appliedDiscountCode = normalizedCode;

    try {
      if (normalizedCode) {
        window.sessionStorage.setItem(
          this.discountStorageKey,
          normalizedCode,
        );
      } else {
        window.sessionStorage.removeItem(
          this.discountStorageKey,
        );
      }
    } catch (error) {
      /*
       * sessionStorage có thể bị chặn.
       * Vẫn giữ mã trong biến của custom element.
       */
    }
  }

  getStoredDiscountCode() {
    if (this.appliedDiscountCode) {
      return this.appliedDiscountCode;
    }

    try {
      return (
        window.sessionStorage.getItem(
          this.discountStorageKey,
        ) || ''
      ).trim();
    } catch (error) {
      return '';
    }
  }

  async checkoutWithDiscount(form, submitter, discount) {
    const checkoutButton =
      submitter instanceof HTMLElement
        ? submitter
        : form.querySelector('[name="checkout"]');

    const normalizedDiscount = String(discount || '').trim();

    if (!normalizedDiscount) {
      this.checkoutSubmitting = true;
      HTMLFormElement.prototype.submit.call(form);
      return;
    }

    this.checkoutSubmitting = true;
    this.setLoading(checkoutButton, true);
    this.setStatus('');

    try {
      /*
       * Đi qua route discount của Shopify rồi redirect thẳng
       * đến checkout. Cách này truyền mã vào checkout rõ ràng,
       * thay vì chỉ submit form /cart sau khi gọi cart/update.js.
       */
      const checkoutUrl = this.getDiscountCheckoutUrl(
        normalizedDiscount,
      );

      window.location.assign(checkoutUrl);
    } catch (error) {
      this.checkoutSubmitting = false;
      this.setLoading(checkoutButton, false);
      this.handleError(error);
    }
  }

  getDiscountCheckoutUrl(discount) {
    const configuredRoot =
      this.dataset.rootUrl ||
      window.Shopify?.routes?.root ||
      '/';

    const localeRoot = configuredRoot.endsWith('/')
      ? configuredRoot
      : `${configuredRoot}/`;

    const normalizedDiscount = String(discount || '').trim();

    /*
     * Shopify discount links always use the domain-root route:
     * /discount/CODE
     *
     * Do not prepend the locale/market root here. For example,
     * /en-us/discount/CODE can redirect without applying the code.
     * The redirect destination can remain locale-aware.
     *
     * Shopify's shareable discount links double-encode special
     * characters. Simple codes such as TEST are unchanged.
     */
    const discountCode = encodeURIComponent(
      encodeURIComponent(normalizedDiscount),
    );

    const discountUrl = new URL(
      `/discount/${discountCode}`,
      window.location.origin,
    );

    discountUrl.searchParams.set(
      'redirect',
      `${localeRoot}checkout`,
    );

    return discountUrl.toString();
  }

  async postJSON(url, body) {
    if (!url) throw new Error(this.dataset.genericError || 'Missing cart endpoint.');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw await this.createRequestError(response);
    return response.json();
  }

  async createRequestError(response) {
    let payload = null;

    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    const fallback = this.dataset.genericError || 'Something went wrong. Please try again.';
    return new Error(payload?.description || payload?.message || `${fallback} (${response.status})`);
  }

  readJSONAttribute(value, fallback) {
    if (!value) return fallback;

    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  escapeSelector(value) {
    return window.CSS?.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
  }

  setItemLoading(item, loading) {
    if (!(item instanceof HTMLElement)) return;

    item.classList.toggle('is-updating', loading);
    item.toggleAttribute('inert', loading);
    item.setAttribute('aria-busy', String(loading));

    if (!loading) {
      item.removeAttribute('aria-busy');
    }
  }

  setLoading(control, loading) {
    if (!control) return;

    control.toggleAttribute('disabled', loading);
    control.setAttribute('aria-busy', String(loading));
  }

  setDrawerBusy(busy) {
    this.toggleAttribute('aria-busy', busy);
  }

  setStatus(message, isError = false) {
    const status = this.querySelector('[data-cart-drawer-status]');
    if (!status) return;

    status.textContent = message || '';
    status.dataset.status = isError ? 'error' : 'success';
  }

  handleError(error) {
    console.error(error);
    this.setStatus(error?.message || this.dataset.genericError, true);
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

window.AmerceCartDrawer = {
  open(trigger) {
    document.querySelector('cart-drawer')?.open(trigger);
  },

  close() {
    document.querySelector('cart-drawer')?.close();
  },

  refresh(open = false) {
    return document.querySelector('cart-drawer')?.refresh(open);
  },

  render(response, open = true) {
    document.querySelector('cart-drawer')?.renderContents(response, open);
  },
};

/*
 * Theme editor only. The drawer renders `hidden` and its only trigger lives in
 * the header, so selecting the section in the sidebar used to change nothing in
 * the preview. Selecting it now opens the drawer; selecting anything else, or
 * deselecting, closes it again, because it is a fixed overlay with a backdrop and
 * would otherwise cover the section the merchant moved on to.
 *
 * Only a drawer this code opened is ever closed again, so one the merchant opened
 * from the header — or one a cart update opened — is left alone. `editorOpen` is
 * scoped to this block rather than kept on the element because the editor replaces
 * the section node on every setting change.
 */
if (window.Shopify && Shopify.designMode) {
  let editorOpen = false;

  const editorEventDrawer = (target) =>
    target instanceof Element ? target.querySelector('cart-drawer') : null;

  const closeFromEditor = () => {
    if (!editorOpen) return;
    editorOpen = false;
    document.querySelector('cart-drawer')?.closeFromEditor?.();
  };

  document.addEventListener('shopify:section:select', (event) => {
    const drawer = editorEventDrawer(event.target);

    if (!drawer) {
      closeFromEditor();
      return;
    }

    editorOpen = true;
    drawer.openFromEditor?.();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    if (editorEventDrawer(event.target)) closeFromEditor();
  });

  /*
   * A reloaded section connects `hidden`, so a drawer being held open for the
   * merchant is re-opened once the replacement element is in place.
   */
  document.addEventListener('shopify:section:load', (event) => {
    const drawer = editorEventDrawer(event.target);
    if (drawer && editorOpen) drawer.openFromEditor?.();
  });
}
